"use client"

import { useCallback, useEffect, useState } from "react"

const STORAGE_KEY = "gacha-reveal-muted"
const MUTED_CHANGE_EVENT = "gacha-reveal-muted-change"
const DEFAULT_MUTED = false

function readMutedPreference(): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === null) return DEFAULT_MUTED
    return stored === "1"
  } catch {
    return DEFAULT_MUTED
  }
}

function writeMutedPreference(nextMuted: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, nextMuted ? "1" : "0")
    window.dispatchEvent(
      new CustomEvent(MUTED_CHANGE_EVENT, { detail: { muted: nextMuted } }),
    )
  } catch {
    // ignore
  }
}

export function useGachaRevealMute() {
  const [muted, setMuted] = useState(readMutedPreference)

  const setMutedPreference = useCallback((nextMuted: boolean) => {
    setMuted(nextMuted)
    writeMutedPreference(nextMuted)
  }, [])

  const toggleMuted = useCallback(() => {
    setMuted((current) => {
      const next = !current
      writeMutedPreference(next)
      return next
    })
  }, [])

  useEffect(() => {
    const handleMutedChange = (event: Event) => {
      const nextMuted = (event as CustomEvent<{ muted?: boolean }>).detail
        ?.muted
      if (typeof nextMuted === "boolean") {
        setMuted(nextMuted)
      }
    }
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setMuted(readMutedPreference())
      }
    }

    window.addEventListener(MUTED_CHANGE_EVENT, handleMutedChange)
    window.addEventListener("storage", handleStorageChange)
    return () => {
      window.removeEventListener(MUTED_CHANGE_EVENT, handleMutedChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  return { muted, setMuted: setMutedPreference, toggleMuted }
}
